import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const CompensationFAQ = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Compensation FAQ" 
        description="Common questions about pay, bonuses, and commissions"
      >
        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">General Pay Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">How is my base pay calculated?</h3>
              <p className="text-muted-foreground">
                You earn <strong>$15 net per pool serviced</strong> (this is $20 gross minus a $5 service fee 
                that covers chemicals, equipment, and platform support). Payment is based on completed jobs 
                verified through the BlueLux Technician App.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What is the mobility stipend?</h3>
              <p className="text-muted-foreground">
                If you use your own vehicle for pool service, you receive <strong>$1.20 per pool</strong> as 
                a mobility stipend to help offset fuel and vehicle costs. If you lease a BlueLux-provided vehicle, 
                you pay $15 per day instead.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">How much can I realistically earn?</h3>
              <p className="text-muted-foreground">
                Your earnings depend on the number of pools you service, specialty services you complete, 
                and bonuses you earn. For example: servicing 100 pools per month = $1,500 base + $120 mobility 
                stipend + specialty services + bonuses. Top performers who maintain quality standards and earn 
                all bonuses can significantly increase their monthly income.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">When do I get paid?</h3>
              <p className="text-muted-foreground">
                Payments are processed <strong>twice monthly</strong> via direct deposit for completed and approved 
                jobs. For any service without a specified payment date, BlueLux remits payment within 30 days of 
                receiving a valid invoice, as required by California law.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What if I don't submit my work through the app?</h3>
              <p className="text-muted-foreground">
                <strong>If it's not logged in the app, it didn't happen.</strong> You will not receive payment 
                for any pool service without proper app documentation (service checklist, water chemistry readings, 
                chemical dosing, and photos). This is the core rule of BlueLux operations.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Specialty Services</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">How much do I earn for filter cleans?</h3>
              <p className="text-muted-foreground">
                You earn <strong>$40 per completed filter clean</strong>. This must be documented in the app 
                with before/after photos and service details.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">How much do I earn for salt cell cleans?</h3>
              <p className="text-muted-foreground">
                You earn <strong>$10 per completed salt cell clean</strong>. Like all services, this requires 
                proper app documentation.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Can I charge customers directly for extra services?</h3>
              <p className="text-muted-foreground">
                <strong>No.</strong> All services must be invoiced through BlueLux. This ensures proper payment 
                tracking and commission calculation. Never accept cash or checks directly from customers.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Commission & Referral Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">How do chemical upsell commissions work?</h3>
              <p className="text-muted-foreground">
                You earn <strong>15% commission on chemical upsells</strong> such as conditioner, phosphate remover, 
                and specialty treatments. Routine service chemicals (included in the $5 service fee) are excluded. 
                Commissions are paid monthly.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">How do I get credit for a customer referral?</h3>
              <p className="text-muted-foreground">
                When a customer you referred signs up and mentions your name, you'll receive <strong>$100 after 
                the referred customer remains active for 90 days</strong>. Make sure they mention your name during 
                signup.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What about technician referrals?</h3>
              <p className="text-muted-foreground">
                If you refer another technician who gets hired, you earn <strong>$100 after they remain employed 
                for 90 days</strong>.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Bonus Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">What is the Quality & Professionalism Bonus?</h3>
              <p className="text-muted-foreground">
                Earn <strong>$100 per month</strong> by meeting ALL requirements: 100% app compliance (checklist, 
                readings, photos), professional appearance (uniform, neat presentation), and zero customer complaints.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">How does the Customer Retention Bonus work?</h3>
              <p className="text-muted-foreground">
                You earn <strong>$50 for each customer who remains active for 12 consecutive months</strong>. 
                This rewards long-term quality service and customer satisfaction.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What is the Safety & Reliability Bonus?</h3>
              <p className="text-muted-foreground">
                Earn <strong>$250 per quarter</strong> by maintaining: zero missed visits, zero accidents, and 
                on-time reporting for all pools. Safety and reliability are essential to BlueLux operations.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What are the tenure bonuses?</h3>
              <p className="text-muted-foreground">
                BlueLux rewards loyalty with tenure bonuses: <strong>$250 at 6 months</strong> of continuous service, 
                <strong>$250 at 12 months</strong>, and <strong>$500 at 24 months</strong>.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Can my per-pool rate increase?</h3>
              <p className="text-muted-foreground">
                Yes! BlueLux has a tiered pay system. Start at $15 net per pool in Year 1, then increase to $16 
                (Year 2) and $17 (Year 3) if retention and quality targets are met. Top performers can earn up to 
                $18 net per pool.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What is the Route Coverage Bonus?</h3>
              <p className="text-muted-foreground">
                When covering another technician's route (vacation/sick leave), you earn <strong>$5 extra per 
                pool</strong> for those jobs in addition to your base pay.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Tax & Business Status Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">Am I an employee or independent contractor?</h3>
              <p className="text-muted-foreground">
                You are an <strong>independent contractor</strong>, not an employee. This means you operate your 
                own business and are responsible for your own taxes, insurance, and business expenses. BlueLux does 
                not withhold income taxes or pay employment taxes on your behalf.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What tax form will I receive?</h3>
              <p className="text-muted-foreground">
                You will receive a <strong>Form 1099-NEC</strong> annually showing all compensation paid to you. 
                You are responsible for reporting this income and paying applicable federal and state taxes.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Do I need to pay quarterly estimated taxes?</h3>
              <p className="text-muted-foreground">
                Yes. As an independent contractor, you're generally required to make <strong>quarterly estimated 
                tax payments</strong> to the IRS and your state tax agency. Consult with a tax professional to 
                determine your specific requirements and payment amounts.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What is self-employment tax?</h3>
              <p className="text-muted-foreground">
                As an independent contractor, you pay <strong>self-employment tax</strong> (approximately 15.3% 
                for Social Security and Medicare). This covers both the employee and employer portions that would 
                be split if you were a W-2 employee. Consult a tax professional for accurate calculations.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Can I deduct business expenses?</h3>
              <p className="text-muted-foreground">
                Yes! As an independent contractor, you can deduct legitimate business expenses such as vehicle 
                expenses (mileage or actual expenses), equipment, tools, insurance, licensing fees, and other 
                costs directly related to your pool service business. Keep detailed records and consult a tax 
                professional for guidance.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Should I form an LLC or other business entity?</h3>
              <p className="text-muted-foreground">
                Many independent contractors choose to form an LLC or other business entity for liability protection 
                and potential tax benefits. However, this is a personal business decision. Consult with a business 
                attorney and/or accountant to determine what's best for your situation.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Still Have Questions?</h2>
          <p>
            If your question isn't answered here, reach out to BlueLux management. We're happy to explain any 
            aspect of your compensation package and independent contractor relationship!
          </p>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default CompensationFAQ;